import { Star, Pencil } from 'lucide-react';

// Thin status strip displayed above the editor. Neutral tone when the open
// document is NOT the Active one for its slot; amber accent + caveat when it
// IS Active (because edits to the Active doc flow downstream into invoices,
// job cards, exports, etc).
//
// onRename: when provided, a pencil button renders inline so the user can
// edit the doc name without bouncing back to the docs-list view.

export interface DocumentBannerProps {
  name: string;
  isActive: boolean;
  onRename?: () => void;
}

export function DocumentBanner({ name, isActive, onRename }: DocumentBannerProps) {
  const renameButton = onRename ? (
    <button
      type="button"
      onClick={onRename}
      aria-label="Rename version"
      title="Rename version"
      className={`ml-auto inline-flex h-6 w-6 items-center justify-center rounded transition-colors ${
        isActive
          ? 'text-amber-700 hover:bg-amber-100'
          : 'text-gray-500 hover:bg-gray-200'
      }`}
    >
      <Pencil className="h-3.5 w-3.5" />
    </button>
  ) : null;

  if (isActive) {
    return (
      <div
        role="status"
        data-testid="document-banner"
        data-variant="active"
        className="flex items-center gap-2 px-3 py-1.5 text-xs border border-amber-200 bg-amber-50 text-amber-900 rounded-md"
      >
        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500 shrink-0" />
        <span className="truncate">
          Editing: <strong className="font-semibold">{name}</strong>{' '}
          <span className="text-amber-700">★</span>
        </span>
        <span className="text-amber-700 truncate">
          — changes affect invoices and exports
        </span>
        {renameButton}
      </div>
    );
  }
  return (
    <div
      role="status"
      data-testid="document-banner"
      data-variant="neutral"
      className="flex items-center gap-2 px-3 py-1.5 text-xs border border-gray-200 bg-gray-50 text-gray-700 rounded-md"
    >
      <span className="truncate">
        Editing: <strong className="font-semibold">{name}</strong>
      </span>
      {renameButton}
    </div>
  );
}
